import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import TextAreaInput from "components/ui/input/TextAreaInput";
import { useCreateBidWinTheme } from "lib/hooks/api/bid/win_themes/useCreateBidWinTheme";
import { useUpdateBidWinTheme } from "lib/hooks/api/bid/win_themes/useUpdateBidWinTheme";
import { DeleteWinThemeModal } from "./DeleteWinThemeModal";

export type WinTheme = {
  id: string;
  title: string;
  description: string;
};

type FormValues = Pick<WinTheme, "title" | "description">;

type Props = {
  bidId: string;
  open: boolean;
  onClose: () => void;
  theme?: WinTheme;
};

export function AddEditWinThemeDrawer({ bidId, open, onClose, theme }: Props) {
  const isEditing = !!theme;

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { title: "", description: "" },
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: createWinTheme, isLoading: isCreating } = useCreateBidWinTheme({ bidId });
  const { mutate: updateWinTheme, isLoading: isUpdating } = useUpdateBidWinTheme({ bidId });

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (open) {
      reset({
        title: theme?.title ?? "",
        description: theme?.description ?? "",
      });
    }
  }, [open, theme, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (values: FormValues) => {
    if (isEditing) {
      updateWinTheme(
        { UpdateBidWinThemeInput: { id: theme.id, ...values } },
        { onSuccess: handleClose },
      );
    } else {
      createWinTheme({ CreateBidWinThemeInput: { bidId, ...values } }, { onSuccess: handleClose });
    }
  };

  return (
    <>
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} padding={24}>
        <Flex column gap={16} height="100%">
          <Flex justifyContent="space-between" alignItems="center">
            <Text h2>{isEditing ? "Edit" : "Create"} win theme</Text>
            <Flex gap={8}>
              {isEditing && (
                <Button
                  icon="trash03"
                  buttonType="tertiary"
                  aria-label="Delete win theme"
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isLoading}
                />
              )}
              <Button
                icon="xClose"
                buttonType="tertiary"
                aria-label="Close drawer"
                onClick={handleClose}
                disabled={isLoading}
              />
            </Flex>
          </Flex>
          <Flex column gap={4}>
            <Text>Win theme title</Text>
            <TextAreaInput name="title" control={control} placeholder="Enter a title" minRows={3} />
          </Flex>
          <Flex column gap={4}>
            <Text>Win theme description</Text>
            <TextAreaInput
              name="description"
              control={control}
              placeholder="Enter a description"
              minRows={3}
            />
          </Flex>
          <Flex gap={8} justifyContent="flex-end">
            {isEditing && (
              <Button buttonType="secondary" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
            )}
            <Button onClick={handleSubmit(onSubmit)} loading={isLoading} disabled={isLoading}>
              {isEditing ? "Save changes" : "Create win theme"}
            </Button>
          </Flex>
        </Flex>
      </Drawer>
      {isEditing && (
        <DeleteWinThemeModal
          bidId={bidId}
          themeId={theme.id}
          isOpen={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
          onSuccess={() => {
            setIsDeleteModalOpen(false);
            handleClose();
          }}
        />
      )}
    </>
  );
}
